import type { Label } from '@/api/types';
import { Icon } from '@/components/Icon';
import { cssVars } from '@/utils/css';
import { labelColorVars } from './useLabels';
import './labels.css';

/** A label's colour dot: decoration, the name beside it carries the meaning. */
export function LabelDot({ label, className }: { label: Pick<Label, 'color'>; className?: string }) {
  return (
    <span
      className={className ? `label-dot ${className}` : 'label-dot'}
      aria-hidden="true"
      style={cssVars(labelColorVars(label.color))}
    />
  );
}

/**
 * A row's labels (plan/17 §5): at most `max` chips and a "+N" for the rest,
 * so a phone row keeps its subject. Text first, colour second — the tint is
 * the shortcut, the name is the signal.
 */
export function RowLabelChips({ labels, max = 3 }: { labels: readonly Label[]; max?: number }) {
  if (labels.length === 0) return null;
  const shown = labels.slice(0, max);
  const rest = labels.length - shown.length;
  return (
    <span className="label-chips message-row__labels">
      <span className="visually-hidden">{`Labels: ${labels.map((label) => label.name).join(', ')}. `}</span>
      {shown.map((label) => (
        <span
          key={label.id}
          className="label-chip"
          aria-hidden="true"
          title={label.name}
          style={cssVars(labelColorVars(label.color))}
        >
          <span className="label-chip__name">{label.name}</span>
        </span>
      ))}
      {rest > 0 && (
        <span className="label-chip label-chip--more" aria-hidden="true" title={labels.slice(max).map((l) => l.name).join(', ')}>
          {`+${rest}`}
        </span>
      )}
    </span>
  );
}

/**
 * The reading pane's labels, each with an × that takes it off (`onRemove`):
 * this message, or in the conversation pane the whole conversation.
 */
export function HeaderLabelChips({
  labels,
  onRemove,
  scope = 'message',
}: {
  labels: readonly Label[];
  onRemove?: ((label: Label) => void) | undefined;
  scope?: 'message' | 'conversation';
}) {
  if (labels.length === 0) return null;
  return (
    <ul className="label-chips label-chips--header" aria-label="Labels">
      {labels.map((label) => (
        <li key={label.id} className="label-chip" style={cssVars(labelColorVars(label.color))}>
          <span className="label-chip__name" title={label.name}>
            {label.name}
          </span>
          {onRemove && (
            <button
              type="button"
              className="label-chip__remove"
              aria-label={`Remove label ${label.name} from this ${scope}`}
              title={`Remove ${label.name}`}
              onClick={() => onRemove(label)}
            >
              <Icon name="close" size={12} />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}
